import React, { FC } from 'react';
import { i18n } from '@lingui/core';
import { Trans, t } from '@lingui/macro';

import {
    Section,
    Container,
    Typography,
    Grid,
    SectionBenefitsCampaigns,
} from 'components';

import Pictorials from 'components/Pictorial/pictorials';

import PhoneGameplay from 'assets/images/black-friday/phone_gameplay.png';
import PhoneSongs from 'assets/images/black-friday/phone_songs.png';
import PhoneArtist from 'assets/images/black-friday/phone_artist.png';
import PhoneProgress from 'assets/images/black-friday/phone_progress.png';

import { Quotes } from 'pages/BlackFriday/Landing/quotes';
import { ImageContainer, ColumnStyled } from 'pages/BlackFriday/Landing/styled';

export { Ratings } from 'pages/BlackFriday/Landing/ratings';

export const Sections: FC<{ isLicensed: boolean }> = ({ isLicensed }) => {
    return isLicensed ? (
        <React.Fragment>
            <Section background="labels">
                <Container>
                    <Grid alignCenter>
                        <ColumnStyled cols={6} tablet={12}>
                            <ImageContainer rightMargin>
                                <img src={PhoneGameplay} alt="" />
                            </ImageContainer>
                        </ColumnStyled>
                        <ColumnStyled $order={1} cols={6} tablet={12}>
                            <Typography
                                variant="h2"
                                spaceBottom="medium"
                                color="semidarkGrey"
                            >
                                <Trans>
                                    The fun and easy way to learn an instrument
                                </Trans>
                            </Typography>

                            <Typography variant="bodyl" color="baseGray">
                                <Trans>
                                    Don’t worry, you can learn to play an
                                    instrument with Yousician regardless of your
                                    skill level. Follow learning paths created
                                    by music experts, learn techniques with
                                    step-by-step lessons and get real-time
                                    feedback while playing your favorite songs.
                                </Trans>
                            </Typography>
                        </ColumnStyled>
                    </Grid>
                </Container>
            </Section>

            <SectionBenefitsCampaigns
                showTitle={false}
                background="white"
                featuresList={[
                    {
                        icon: Pictorials.AllInstruments,
                        name: i18n._(t`Learn all 5 instruments`),
                        description: i18n._(
                            t`Learn <b>guitar, ukulele, bass, piano, or vocals</b> with full access to lessons and songs for every instrument.`,
                        ),
                    },
                    {
                        icon: Pictorials.Play,
                        name: i18n._(t`Play as much <br> as you want`),
                        description: i18n._(
                            t`Learn at your own pace with unlimited, interruption-free practice time.`,
                        ),
                    },
                    {
                        icon: Pictorials.Guitars,
                        name: i18n._(t`You play, the app listens`),
                        description: i18n._(
                            t`Play along with fun interactive lessons, get real-time feedback, and improve your skills.`,
                        ),
                    },
                ]}
            />

            <Section background="labels">
                <Container>
                    <Grid alignCenter>
                        <ColumnStyled cols={6} tablet={12}>
                            <Typography
                                variant="h2"
                                spaceBottom="medium"
                                color="semidarkGrey"
                            >
                                <Trans>Explore 10,000+ songs and lessons</Trans>
                            </Typography>

                            <Typography variant="bodyl" color="baseGray">
                                <Trans>
                                    Learn to play your favorite songs by artists
                                    you love with unlimited access to our
                                    library of 10,000+ popular songs, lessons
                                    and exercises. It’s updated weekly, so
                                    there’s always something new to discover!
                                </Trans>
                            </Typography>
                        </ColumnStyled>
                        <ColumnStyled cols={6} tablet={12} $order={-1}>
                            <ImageContainer leftMargin>
                                <img src={PhoneSongs} alt="" />
                            </ImageContainer>
                        </ColumnStyled>
                    </Grid>
                </Container>
            </Section>

            <Quotes />

            <Section background="labels">
                <Container>
                    <Grid alignCenter>
                        <ColumnStyled cols={6} tablet={12}>
                            <ImageContainer rightMargin>
                                <img src={PhoneArtist} alt="" />
                            </ImageContainer>
                        </ColumnStyled>
                        <ColumnStyled cols={6} tablet={12}>
                            <Typography
                                variant="h2"
                                spaceBottom="medium"
                                color="semidarkGrey"
                            >
                                <Trans>
                                    Get tips and tricks from artists you love
                                </Trans>
                            </Typography>

                            <Typography variant="bodyl" color="baseGray">
                                <Trans>
                                    Learn and play with your favorite artists in
                                    all-new Spotlight courses. Get tips,
                                    stories, exclusive performances and more
                                    from world-famous musicians like Jason Mraz
                                    and Juanes. Next up? Metallica!
                                </Trans>
                            </Typography>
                        </ColumnStyled>
                    </Grid>
                </Container>
            </Section>

            <Section background="white">
                <Container>
                    <Grid alignCenter>
                        <ColumnStyled cols={6} tablet={12}>
                            <Typography
                                variant="h2"
                                spaceBottom="medium"
                                color="semidarkGrey"
                            >
                                <Trans>
                                    Learn together with Premium+ Family
                                </Trans>
                            </Typography>

                            <Typography variant="bodyl" color="baseGray">
                                <Trans>
                                    Four accounts, one easy plan. Each family
                                    member can learn at their own pace, with
                                    personal progress tracking and full access
                                    to Premium+ benefits.
                                </Trans>
                            </Typography>
                        </ColumnStyled>
                        <ColumnStyled cols={6} tablet={12} $order={-1}>
                            <ImageContainer leftMargin>
                                <img src={PhoneProgress} alt="" />
                            </ImageContainer>
                        </ColumnStyled>
                    </Grid>
                </Container>
            </Section>
        </React.Fragment>
    ) : (
        <React.Fragment>
            <SectionBenefitsCampaigns
                showTitle={false}
                background="white"
                featuresList={[
                    {
                        icon: Pictorials.AllInstruments,
                        name: i18n._(t`Learn all 5 instruments`),
                        description: i18n._(
                            t`Learn <b>guitar, ukulele, bass, piano, or vocals</b> with full access to lessons and songs for every instrument.`,
                        ),
                    },
                    {
                        icon: Pictorials.Play,
                        name: i18n._(t`Play as much <br> as you want`),
                        description: i18n._(
                            t`Learn at your own pace with unlimited, interruption-free practice time.`,
                        ),
                    },
                    {
                        icon: Pictorials.Guitars,
                        name: i18n._(t`You play, the app listens`),
                        description: i18n._(
                            t`Play along with fun interactive lessons, get real-time feedback, and improve your skills.`,
                        ),
                    },
                ]}
            />
            <Section background="labels">
                <Container>
                    <Grid alignCenter>
                        <ColumnStyled cols={6} tablet={12}>
                            <ImageContainer rightMargin>
                                <img src={PhoneGameplay} alt="" />
                            </ImageContainer>
                        </ColumnStyled>
                        <ColumnStyled $order={1} cols={6} tablet={12}>
                            <Typography
                                variant="h2"
                                spaceBottom="medium"
                                color="semidarkGrey"
                            >
                                <Trans>
                                    The fun and easy way to learn an instrument
                                </Trans>
                            </Typography>

                            <Typography variant="bodyl" color="baseGray">
                                <Trans>
                                    Don’t worry, you can learn to play an
                                    instrument with Yousician regardless of your
                                    skill level. Follow learning paths created
                                    by music experts, learn techniques with
                                    step-by-step lessons and get real-time
                                    feedback while playing your favorite songs.
                                </Trans>
                            </Typography>
                        </ColumnStyled>
                    </Grid>
                </Container>
            </Section>
        </React.Fragment>
    );
};
